import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert, Button, DiscordIcon } from '@playerbase/ui';
import {
  AcceptInviteResultSchema,
  InviteValidationResultSchema,
  type InviteValidationResult,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { useAuth } from '../auth/use-auth.js';
import { clearStashedInviteToken, stashInviteToken } from '../auth/invite-redemption.js';
import { getSiteUrl } from '../lib/site-url.js';
import { AuthShell } from './auth-shell.js';

function statusCopy(status: 'expired' | 'revoked' | 'accepted' | 'not_found'): { title: string; body: string } {
  switch (status) {
    case 'expired':
      return { title: 'This invite has expired', body: 'Ask the studio admin to send you a fresh invite.' };
    case 'revoked':
      return { title: 'This invite has been revoked', body: 'The studio admin revoked this invite. Ask them to send a new one if you still need access.' };
    case 'accepted':
      return { title: 'This invite has already been accepted', body: 'If that was you, sign in to access the studio.' };
    case 'not_found':
      return { title: 'Invite link not found', body: 'The link may have been mistyped. Double-check with whoever sent it.' };
  }
}

export function InviteAcceptPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { status: authStatus, user, refreshMemberships, setActiveStudioId, signOut } = useAuth();

  const [validation, setValidation] = useState<InviteValidationResult | null>(null);
  const [validating, setValidating] = useState(true);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptedStudio, setAcceptedStudio] = useState<{ id: string; name: string; shortId: string } | null>(null);

  const validate = useCallback(async (): Promise<void> => {
    if (token === undefined) {
      setValidation({ status: 'not_found' });
      setValidating(false);
      return;
    }
    const { data, error } = await supabase.rpc('validate_studio_invite', { p_token: token });
    if (error !== null) {
      setValidation({ status: 'not_found' });
      setValidating(false);
      return;
    }
    const parsed = InviteValidationResultSchema.safeParse(data);
    if (!parsed.success) {
      setValidation({ status: 'not_found' });
    } else {
      setValidation(parsed.data);
    }
    setValidating(false);
  }, [token]);

  useEffect(() => {
    void validate();
  }, [validate]);

  async function handleAccept(): Promise<void> {
    if (token === undefined) return;
    setAcceptError(null);
    setAccepting(true);
    try {
      const { data, error } = await supabase.rpc('accept_studio_invite', { p_token: token });
      if (error !== null) {
        setAcceptError(error.message);
        return;
      }
      const parsed = AcceptInviteResultSchema.safeParse(data);
      if (!parsed.success) {
        setAcceptError('Invite accepted but the response was malformed. Refresh to see your new studio.');
        return;
      }
      await refreshMemberships();
      // The accept RPC returns id/name/slug; the URL keys on short_id, so
      // resolve it directly (the caller is now a member, so RLS allows it).
      const { data: row } = await supabase
        .from('studios')
        .select('short_id')
        .eq('id', parsed.data.studio.id)
        .single();
      if (row === null || typeof row.short_id !== 'string') {
        setAcceptError('Invite accepted but the studio could not be resolved. Refresh to see your new studio.');
        return;
      }
      setAcceptedStudio({
        id: parsed.data.studio.id,
        name: parsed.data.studio.name,
        shortId: row.short_id,
      });
    } finally {
      setAccepting(false);
    }
  }

  function handleSwitch(): void {
    if (acceptedStudio === null) return;
    setActiveStudioId(acceptedStudio.id);
    navigate(`/s/${acceptedStudio.shortId}`, { replace: true });
  }

  async function handleDiscordSignUp(): Promise<void> {
    if (token !== undefined) stashInviteToken(token);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        scopes: 'identify email',
        redirectTo: `${getSiteUrl()}/auth/callback`,
      },
    });
    if (error !== null) {
      setAcceptError(error.message);
      clearStashedInviteToken();
    }
  }

  function handleEmailSignUp(): void {
    if (token !== undefined) stashInviteToken(token);
    // Pass the token through the URL too so /sign-up can attach it to the
    // user's auth metadata. That lets us survive cross-browser email-confirm
    // flows where localStorage is wiped between sign-up submit and email
    // verification.
    navigate(token !== undefined ? `/sign-up?invite=${encodeURIComponent(token)}` : '/sign-up');
  }

  function handleLogIn(): void {
    if (token !== undefined) stashInviteToken(token);
    // /sign-in reads ?invite and routes back here after a successful login,
    // where the logged-in branch shows the Accept button.
    navigate(token !== undefined ? `/sign-in?invite=${encodeURIComponent(token)}` : '/sign-in');
  }

  // ---- render --------------------------------------------------------------

  if (validating || authStatus === 'loading') {
    return (
      <AuthShell title="Loading invite…" subtitle="Checking your invite link.">
        <p className="text-sm text-text-muted">One moment.</p>
      </AuthShell>
    );
  }

  if (validation === null || validation.status !== 'valid') {
    if (validation === null) {
      return (
        <AuthShell title="Could not load invite" subtitle="Please try again.">
          <Link to="/sign-in" className="text-sm text-accent underline">Back to sign in</Link>
        </AuthShell>
      );
    }
    const copy = statusCopy(validation.status);
    return (
      <AuthShell title={copy.title} subtitle={copy.body}>
        <div className="flex flex-col gap-3">
          <Link to="/sign-in" className="text-sm text-accent underline">Back to sign in</Link>
        </div>
      </AuthShell>
    );
  }

  // Post-accept success state for existing users.
  if (acceptedStudio !== null) {
    return (
      <AuthShell
        title={`Joined ${acceptedStudio.name}`}
        subtitle="You're now a member."
      >
        <div className="flex flex-col gap-3">
          <Button variant="primary" onClick={handleSwitch} className="w-full">
            Switch to {acceptedStudio.name}
          </Button>
          <Link to="/" className="text-center text-sm text-text-muted underline">
            Stay on my current screen
          </Link>
        </div>
      </AuthShell>
    );
  }

  const v = validation;
  const personalBlock = v.personalMessage !== null && v.personalMessage.trim() !== ''
    ? (
      <blockquote className="rounded-md border-l-2 border-accent bg-surface-2 px-3 py-2 text-sm italic text-text-muted">
        {v.personalMessage}
      </blockquote>
    )
    : null;

  // Logged out: show sign-up surface (skips the studio-setup form post-auth).
  if (authStatus === 'unauthenticated' || user === null) {
    return (
      <AuthShell
        title={`Sign up to join ${v.studio.name}`}
        subtitle={`${v.inviter.displayName} invited you. Pick how you'd like to sign up.`}
      >
        <div className="flex flex-col gap-4">
          {personalBlock}
          {acceptError !== null && <Alert tone="danger">{acceptError}</Alert>}
          <Button variant="discord" className="w-full" onClick={() => { void handleDiscordSignUp(); }}>
            <DiscordIcon className="h-4 w-auto" />
            Sign up with Discord
          </Button>
          <div className="relative flex items-center gap-3">
            <div className="flex-1 border-t border-border" />
            <span className="text-xs text-text-muted">or</span>
            <div className="flex-1 border-t border-border" />
          </div>
          <Button variant="ghost" className="w-full" onClick={handleEmailSignUp}>
            Sign up with email
          </Button>
          <p className="text-center text-sm text-text-muted">
            Already have an account?{' '}
            <button
              type="button"
              className="text-accent underline underline-offset-2 hover:opacity-80 transition-opacity"
              onClick={handleLogIn}
            >
              Log in
            </button>
          </p>
          <p className="text-center text-xs text-text-muted">
            Sent to {v.email}. You can sign up with any account - the invite link is what proves it's yours.
          </p>
        </div>
      </AuthShell>
    );
  }

  // Logged-in, valid invite.
  const wrongAccount = user.email !== undefined && user.email !== null && user.email.toLowerCase() !== v.email.toLowerCase();
  return (
    <AuthShell
      title={`Join ${v.studio.name}`}
      subtitle={`${v.inviter.displayName} invited you to PlayerBase.`}
    >
      <div className="flex flex-col gap-4">
        {personalBlock}
        {acceptError !== null && <Alert tone="danger">{acceptError}</Alert>}
        {wrongAccount && (
          <p className="text-xs text-text-muted">
            The invite was sent to <strong>{v.email}</strong> but you're signed in as <strong>{user.email}</strong>. You can accept anyway, or
            {' '}
            <button
              type="button"
              className="text-accent underline"
              onClick={() => { void signOut(); }}
            >
              sign out and use a different account
            </button>
            .
          </p>
        )}
        <Button variant="primary" className="w-full" onClick={() => { void handleAccept(); }} disabled={accepting}>
          {accepting ? 'Accepting…' : 'Accept invite'}
        </Button>
      </div>
    </AuthShell>
  );
}
